import { scoreQueryOptions } from "../scores/scoreQueryOptions";
/**
 * ISSUE-1188. The player page's season reads: sports-api
 * /player/season-stats-get for Stats (only with the roster-resolved playerId)
 * and events-api /event/player-game-log-get for Game log (the roster id, else
 * the URL id, which may be a box score minted id). The season id is the roster
 * read's leagueSeasonId. A failed read opens the shared error modal.
 */
import { useEventPlayerGameLogGet } from "@sportsmet/events-sdk/private";
import {
  usePlayerSeasonStatsGet,
  useTeamRosterGet,
} from "@sportsmet/sports-sdk/private";
import { useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import type {
  typePlayerProfileView,
  typePlayerRecord,
} from "./playerProfileReader";
import {
  toPlayerGameLogState,
  toPlayerStatsState,
  type typePlayerSeasonView,
} from "./playerSeasonView";

export function usePlayerSeasonData(input: {
  view: typePlayerProfileView | null;
  record: typePlayerRecord | null;
}): typePlayerSeasonView {
  const teamId = input.view?.teamId ?? null;
  const roster = useTeamRosterGet(
    { teamId: teamId ?? "" },
    { enabled: teamId !== null },
  );
  const seasonId = roster.data?.data?.leagueSeasonId ?? null;

  const statsPlayerId = input.record?.playerId ?? null;
  const stats = usePlayerSeasonStatsGet(
    { playerId: statsPlayerId ?? "" },
    {
      ...scoreQueryOptions,
      enabled: statsPlayerId !== null,
      refetchInterval: 120_000,
    },
  );

  const gameLogPlayerId =
    input.record?.playerId ?? input.view?.playerId ?? null;
  const gameLog = useEventPlayerGameLogGet(
    {
      playerId: gameLogPlayerId ?? "",
      teamId: teamId ?? "",
      seasonId: seasonId ?? "",
      timeZone: resolveViewerTimeZone(),
    },
    {
      ...scoreQueryOptions,
      refetchInterval: 120_000,
      enabled: gameLogPlayerId !== null && teamId !== null && seasonId !== null,
    },
  );

  // Each effect fires once per new answer (dataUpdatedAt) and team, not on
  // every render holding the same data (same rule as usePlayerProfileData).
  useEffect(() => {
    if (stats.data?.data == null && extractErrorCode(stats.data) !== null) {
      showApiError(stats.data, undefined, stats.dataUpdatedAt);
    }
  }, [stats.dataUpdatedAt, teamId]);

  useEffect(() => {
    if (gameLog.data?.data == null && extractErrorCode(gameLog.data) !== null) {
      showApiError(gameLog.data, undefined, gameLog.dataUpdatedAt);
    }
  }, [gameLog.dataUpdatedAt, teamId]);

  return {
    stats: toPlayerStatsState(stats.data?.data),
    gameLog: toPlayerGameLogState(gameLog.data?.data),
  };
}
